import { fetchApi } from '../client';
import { captureStaffSession } from '../sessionToken';

export interface MfaSetupResponse {
  secret: string;
  otpauthUri: string;
  qrCodeBase64: string;
}

export interface MfaStatusResponse {
  enabled: boolean;
  backupCodesRemaining: number;
}

export const mfaModule = {
  /**
   * Begin enrollment. The server keeps the generated secret for 10 minutes and confirm() checks the
   * code against that copy; the secret in this response is only for manual entry (medopd-docs 96).
   * Refused while MFA is already on — turn it off first (needs a code), then set it up again.
   */
  setup: () => fetchApi<MfaSetupResponse>('/api/auth/mfa/setup', { method: 'POST' }),

  /** Confirm enrollment with a code from the authenticator app. Returns 10 backup codes, shown once. */
  confirm: (code: string) =>
    fetchApi<{ backupCodes: string[] }>('/api/auth/mfa/confirm', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  /**
   * Complete a login that returned mfaRequired — NOT authenticated yet, called pre-session.
   *
   * Captures the returned access token into secureStorage, mirroring `authModule.login()` and
   * `authModule.refresh()`. This is the point at which an MFA login actually becomes a session, so
   * it has the same obligation they do.
   *
   * Without this the session survived only as long as the tab was not reloaded: the backend also
   * sets httpOnly auth/refresh cookies, so every API call kept working and the app looked fine —
   * but `AuthContext.initializeAuth()` gates on `secureStorage.getAuthToken()`, which was never
   * populated. On refresh it read null, cleared the session and bounced the user to /login. The
   * failure was invisible until reload, and only on MFA-enabled accounts, because the non-MFA path
   * stores the token correctly.
   */
  verify: async (mfaPendingToken: string, code?: string, backupCode?: string) => {
    const response = await fetchApi<any>('/api/auth/mfa/verify', {
      method: 'POST',
      skipAuth: true,
      skipBodyLog: true,
      body: JSON.stringify({ mfaPendingToken, code, backupCode }),
    });
    captureStaffSession(response);
    return response;
  },

  status: () => fetchApi<MfaStatusResponse>('/api/auth/mfa/status'),

  /** Step-up: requires a currently-valid code, not just an authenticated session. */
  disable: (code: string) =>
    fetchApi<null>('/api/auth/mfa/disable', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  regenerateBackupCodes: (code: string) =>
    fetchApi<{ backupCodes: string[] }>('/api/auth/mfa/regenerate-backup-codes', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  /** clinic_admin/super_admin only — resets (disables) MFA for a user in their own clinic. */
  adminReset: (userId: string) =>
    fetchApi<null>(`/api/auth/mfa/admin/users/${userId}/reset`, { method: 'POST' }),
};
